import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Sidebar from '../components/shared/Sidebar';
import googleReviewsService from '../services/googleReviewsService';
import {
  GoogleReviewListItem,
  GoogleReviewsDashboardData,
  GoogleReviewsRunHistoryItem,
  GoogleReviewsScrapeActionResult
} from '../types/googleReviews';
import './GoogleReviewsOpsPage.css';

type ScrapedBusinessCard = {
  key: string;
  businessName: string;
  datasetId?: string;
};

const normalizeCardValue = (value: string | null | undefined): string => (value || '').trim();

const buildBusinessCardKey = (businessName: string, datasetId?: string): string =>
  `${businessName.toLowerCase()}::${(datasetId || '').toLowerCase()}`;

const deriveBusinessesFromScrapeResult = (scrapeResult: GoogleReviewsScrapeActionResult): ScrapedBusinessCard[] => {
  const cardByKey = new Map<string, ScrapedBusinessCard>();

  const addCard = (businessNameInput: string | null | undefined, datasetIdInput?: string | null) => {
    const businessName = normalizeCardValue(businessNameInput);
    const datasetId = normalizeCardValue(datasetIdInput) || undefined;
    if (!businessName || !datasetId) return;

    const key = buildBusinessCardKey(businessName, datasetId);
    if (!cardByKey.has(key)) {
      cardByKey.set(key, { key, businessName, datasetId });
    }
  };

  if ('results' in scrapeResult && Array.isArray((scrapeResult as any).results)) {
    (scrapeResult as any).results.forEach((result: any) => {
      addCard(result.data?.name || result.query, result.datasetId);
    });
  } else {
    const single = scrapeResult as any;
    addCard(single.data?.name, single.datasetId);
  }

  return Array.from(cardByKey.values());
};

const GoogleReviewsOpsPage: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<GoogleReviewsDashboardData | null>(null);
  const [runs, setRuns] = useState<GoogleReviewsRunHistoryItem[]>([]);
  const [reviews, setReviews] = useState<GoogleReviewListItem[]>([]);

  const [runsPage, setRunsPage] = useState(1);
  const [runsTotalPages, setRunsTotalPages] = useState(1);
  const [reviewsPage, setReviewsPage] = useState(1);
  const [reviewsTotalPages, setReviewsTotalPages] = useState(1);

  const [reviewsSearch, setReviewsSearch] = useState('');
  const [reviewsBusinessName, setReviewsBusinessName] = useState('');
  const [reviewsDatasetId, setReviewsDatasetId] = useState('');
  const [reviewsMinRating, setReviewsMinRating] = useState('');

  const [scrapeInput, setScrapeInput] = useState('');
  const [maxReviews, setMaxReviews] = useState(100);
  const [runSentimentAtScrape, setRunSentimentAtScrape] = useState(false);
  const [latestScrapedBusinesses, setLatestScrapedBusinesses] = useState<ScrapedBusinessCard[]>([]);
  const [selectedBusinessCardKey, setSelectedBusinessCardKey] = useState<string | null>(null);
  const [deletedBusinessCardKeys, setDeletedBusinessCardKeys] = useState<string[]>([]);

  const [selectedReview, setSelectedReview] = useState<GoogleReviewListItem | null>(null);

  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [runningScrape, setRunningScrape] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  const fallbackBusinesses = useMemo<ScrapedBusinessCard[]>(() => {
    const cardByKey = new Map<string, ScrapedBusinessCard>();

    const addCard = (businessNameInput: string | null | undefined, datasetIdInput?: string | null) => {
      const businessName = normalizeCardValue(businessNameInput);
      const datasetId = normalizeCardValue(datasetIdInput) || undefined;
      if (!businessName || !datasetId) return;

      const key = buildBusinessCardKey(businessName, datasetId);
      if (!cardByKey.has(key)) {
        cardByKey.set(key, { key, businessName, datasetId });
      }
    };

    reviews.forEach((review) => addCard(review.businessName, review.datasetId));
    runs.forEach((run) => addCard(run.businessName, run.datasetId));

    return Array.from(cardByKey.values());
  }, [reviews, runs]);

  const scrapedBusinessCards = useMemo<ScrapedBusinessCard[]>(
    () => {
      const base = latestScrapedBusinesses.length ? latestScrapedBusinesses : fallbackBusinesses;
      if (!deletedBusinessCardKeys.length) return base;
      const deletedSet = new Set(deletedBusinessCardKeys);
      return base.filter((card) => !deletedSet.has(card.key));
    },
    [latestScrapedBusinesses, fallbackBusinesses, deletedBusinessCardKeys]
  );

  const selectedBusinessCard = useMemo(
    () => scrapedBusinessCards.find((card) => card.key === selectedBusinessCardKey) || null,
    [scrapedBusinessCards, selectedBusinessCardKey]
  );

  const effectiveReviewsBusinessName = selectedBusinessCard?.businessName || reviewsBusinessName;
  const effectiveReviewsDatasetId = selectedBusinessCard
    ? (selectedBusinessCard.datasetId || '')
    : reviewsDatasetId;
  const isBusinessSelectionLocked = Boolean(selectedBusinessCard);

  const loadDashboard = useCallback(async () => {
    setLoadingDashboard(true);
    try {
      const data = await googleReviewsService.getDashboardData(
        selectedBusinessCard
          ? {
            datasetId: selectedBusinessCard.datasetId,
            businessName: selectedBusinessCard.businessName
          }
          : undefined
      );
      setDashboardData(data);
    } catch (loadError: any) {
      setError(loadError.message || 'Failed to load Google Reviews dashboard data');
    } finally {
      setLoadingDashboard(false);
    }
  }, [selectedBusinessCard]);

  const loadRuns = useCallback(async () => {
    setLoadingRuns(true);
    try {
      const result = await googleReviewsService.getRunsHistory({
        page: runsPage,
        pageSize: 10,
        businessName: effectiveReviewsBusinessName || undefined,
        datasetId: effectiveReviewsDatasetId || undefined
      });
      setRuns(result.data);
      setRunsTotalPages(result.meta.totalPages || 1);
    } catch (loadError: any) {
      setError(loadError.message || 'Failed to load runs history');
    } finally {
      setLoadingRuns(false);
    }
  }, [runsPage, effectiveReviewsBusinessName, effectiveReviewsDatasetId]);

  const loadReviews = useCallback(async () => {
    setLoadingReviews(true);
    try {
      const result = await googleReviewsService.getReviews({
        page: reviewsPage,
        pageSize: 20,
        businessName: effectiveReviewsBusinessName || undefined,
        datasetId: effectiveReviewsDatasetId || undefined,
        search: reviewsSearch || undefined,
        minRating: reviewsMinRating ? Number(reviewsMinRating) : undefined
      });
      setReviews(result.data);
      setReviewsTotalPages(result.meta.totalPages || 1);
    } catch (loadError: any) {
      setError(loadError.message || 'Failed to load reviews');
    } finally {
      setLoadingReviews(false);
    }
  }, [reviewsPage, effectiveReviewsBusinessName, effectiveReviewsDatasetId, reviewsSearch, reviewsMinRating]);

  useEffect(() => {
    setError(null);
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    setError(null);
    loadRuns();
  }, [loadRuns]);

  useEffect(() => {
    setError(null);
    loadReviews();
  }, [loadReviews]);

  const summaryStats = useMemo(() => {
    const uniqueBusinesses = new Set(reviews.map((review) => review.businessName)).size;
    return {
      pageReviewCount: reviews.length,
      pageBusinessCount: uniqueBusinesses
    };
  }, [reviews]);

  const dashboardAverageGoogleStars = dashboardData?.totals.averageRating;
  const dashboardAverageLuminaScore = dashboardData?.latestAnalysis?.averageLuminaScore ?? null;
  const selectedCardSentimentSummary = normalizeCardValue(dashboardData?.latestAnalysis?.summary);

  const handleScrape = async (event: React.FormEvent) => {
    event.preventDefault();
    const queries = scrapeInput
      .split('\n')
      .map((value) => value.trim())
      .filter(Boolean);
    if (!queries.length) {
      setError('Add at least one business query to scrape.');
      return;
    }

    setRunningScrape(true);
    setError(null);
    setActionMessage(null);
    try {
      let scrapeResult: GoogleReviewsScrapeActionResult;
      if (queries.length === 1) {
        scrapeResult = await googleReviewsService.scrape({
          query: queries[0],
          maxReviews,
          sentiment: runSentimentAtScrape
        });

        setActionMessage(
          runSentimentAtScrape
            ? `Scrape completed for "${queries[0]}". Sentiment is being handled by the scrape pipeline.`
            : `Scrape completed for "${queries[0]}".`
        );

        setReviewsBusinessName(queries[0]);
      } else {
        scrapeResult = await googleReviewsService.scrape({
          queries,
          maxReviews,
          sentiment: runSentimentAtScrape,
          batchSize: 10
        });

        setActionMessage(
          runSentimentAtScrape
            ? `Batch scrape completed for ${queries.length} businesses. Sentiment is being handled by the scrape pipeline.`
            : `Batch scrape completed for ${queries.length} businesses.`
        );
      }
      setLatestScrapedBusinesses(deriveBusinessesFromScrapeResult(scrapeResult));
      setSelectedBusinessCardKey(null);
      setDeletedBusinessCardKeys([]);
      setRunsPage(1);
      setReviewsPage(1);
      await Promise.all([loadDashboard(), loadRuns(), loadReviews()]);
    } catch (actionError: any) {
      setError(actionError.message || 'Scrape request failed');
    } finally {
      setRunningScrape(false);
    }
  };

  const handleSelectBusinessCard = (card: ScrapedBusinessCard) => {
    // If cards are coming from fallback data, freeze the full set before applying
    // selection so selecting one business does not collapse the visible card list.
    if (!latestScrapedBusinesses.length && scrapedBusinessCards.length) {
      setLatestScrapedBusinesses(scrapedBusinessCards);
    }
    setSelectedBusinessCardKey(card.key);
    setReviewsPage(1);
  };

  const handleClearBusinessCardSelection = () => {
    setSelectedBusinessCardKey(null);
    setReviewsPage(1);
  };

  const handleDeleteBusinessCard = async (card: ScrapedBusinessCard) => {
    const shouldDelete = window.confirm(`Are you sure you want to delete ${card.businessName}?`);
    if (!shouldDelete) return;

    const businessName = normalizeCardValue(card.businessName) || undefined;
    const datasetId = normalizeCardValue(card.datasetId) || undefined;
    if (!businessName && !datasetId) {
      setError('Unable to delete business data: missing business name and dataset ID.');
      return;
    }

    setError(null);
    setActionMessage(null);
    try {
      await googleReviewsService.deleteBusinessData({ businessName, datasetId });

      if (selectedBusinessCardKey === card.key) {
        setSelectedBusinessCardKey(null);
        setReviewsPage(1);
      }

      setLatestScrapedBusinesses((prev) => prev.filter((item) => item.key !== card.key));
      setDeletedBusinessCardKeys((prev) => prev.filter((key) => key !== card.key));

      await Promise.all([loadDashboard(), loadRuns(), loadReviews()]);
      setActionMessage(`Deleted data for "${card.businessName}" successfully.`);
    } catch (deleteError: any) {
      setError(deleteError.message || `Failed to delete data for "${card.businessName}".`);
    }
  };

  return (
    <div className="dashboard-page">
      <Sidebar />
      <div className="dashboard-main">
        <div className="dashboard-content">
          <div className="page-header">
            <h1>Google Reviews Ops</h1>
          </div>

          {error && <div className="google-reviews-ops-alert google-reviews-ops-alert-error">{error}</div>}
          {actionMessage && <div className="google-reviews-ops-alert google-reviews-ops-alert-success">{actionMessage}</div>}

          <section className="google-reviews-ops-card">
            <h3>Scrape Input</h3>
            <form onSubmit={handleScrape} className="google-reviews-ops-form">
              <textarea
                value={scrapeInput}
                onChange={(event) => setScrapeInput(event.target.value)}
                placeholder="Enter one business query per line"
                rows={4}
                className="google-reviews-ops-textarea"
              />
              <div className="google-reviews-ops-form-row">
                <label>
                  Max reviews
                  <input
                    type="number"
                    min={1}
                    max={500}
                    value={maxReviews}
                    onChange={(event) => setMaxReviews(Number(event.target.value))}
                  />
                </label>
                <label className="google-reviews-ops-checkbox">
                  <input
                    type="checkbox"
                    checked={runSentimentAtScrape}
                    onChange={(event) => setRunSentimentAtScrape(event.target.checked)}
                  />
                  Run AI sentiment during scrape
                </label>
                <button type="submit" disabled={runningScrape}>
                  {runningScrape ? 'Starting...' : 'Start Scrape'}
                </button>
              </div>
            </form>
          </section>

          <section className="google-reviews-ops-card">
            <div className="google-reviews-ops-section-header">
              <h3>Scraped Businesses</h3>
              {selectedBusinessCard && (
                <button type="button" onClick={handleClearBusinessCardSelection}>
                  Clear selection
                </button>
              )}
            </div>

            {scrapedBusinessCards.length === 0 ? (
              <p className="google-reviews-ops-empty">
                Run a scrape to populate business cards. Fallback cards appear from loaded reviews/runs when available.
              </p>
            ) : (
              <div className="google-reviews-ops-business-cards">
                {scrapedBusinessCards.map((card) => (
                  <div
                    key={card.key}
                    className={`google-reviews-ops-business-card ${selectedBusinessCard?.key === card.key ? 'is-selected' : ''}`}
                  >
                    <button
                      type="button"
                      className="google-reviews-ops-business-card-delete"
                      onClick={() => handleDeleteBusinessCard(card)}
                      aria-label={`Delete ${card.businessName}`}
                      title={`Delete ${card.businessName}`}
                    >
                      x
                    </button>
                    <button
                      type="button"
                      className="google-reviews-ops-business-card-select"
                      onClick={() => handleSelectBusinessCard(card)}
                    >
                      <strong>{card.businessName}</strong>
                      <span>Dataset: {card.datasetId || 'N/A'}</span>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="google-reviews-ops-grid">
            <div className="google-reviews-ops-card">
              <h3>Business Summary</h3>
              {loadingDashboard ? (
                <p>Loading summary...</p>
              ) : (
                <div className="google-reviews-ops-kpis">
                  <div>
                    <span>Total Reviews</span>
                    <strong>{dashboardData?.totals.totalReviews ?? 0}</strong>
                  </div>
                  <div>
                    <span>Total Businesses</span>
                    <strong>{dashboardData?.totals.totalBusinesses ?? 0}</strong>
                  </div>
                  <div>
                    <span>Avg Google Stars (Dashboard)</span>
                    <strong>{dashboardAverageGoogleStars ?? '-'}</strong>
                  </div>
                  <div>
                    <span>Avg Lumina Score (Dashboard)</span>
                    <strong>{dashboardAverageLuminaScore ?? '-'}</strong>
                  </div>
                  <div>
                    <span>Page Reviews</span>
                    <strong>{summaryStats.pageReviewCount}</strong>
                  </div>
                  <div>
                    <span>Page Businesses</span>
                    <strong>{summaryStats.pageBusinessCount}</strong>
                  </div>
                </div>
              )}
            </div>

            <div className="google-reviews-ops-card">
              <h3>Sentiment Summary</h3>
              {loadingDashboard ? (
                <p>Loading sentiment summary...</p>
              ) : !selectedBusinessCard ? (
                <p className="google-reviews-ops-empty">Select a scraped business card to view sentiment summary.</p>
              ) : selectedCardSentimentSummary ? (
                <div className="google-reviews-ops-sentiment">
                  <p>{selectedCardSentimentSummary}</p>
                </div>
              ) : (
                <p className="google-reviews-ops-empty">No sentiment summary found for selected business.</p>
              )}
            </div>
          </section>

          <section className="google-reviews-ops-card">
            <h3>Reviews</h3>
            <div className="google-reviews-ops-filters">
              <input
                type="text"
                placeholder="Business"
                value={reviewsBusinessName}
                onChange={(event) => setReviewsBusinessName(event.target.value)}
                disabled={isBusinessSelectionLocked}
              />
              <input
                type="text"
                placeholder="Dataset ID"
                value={reviewsDatasetId}
                onChange={(event) => setReviewsDatasetId(event.target.value)}
                disabled={isBusinessSelectionLocked}
              />
              <input
                type="text"
                placeholder="Search text or author"
                value={reviewsSearch}
                onChange={(event) => setReviewsSearch(event.target.value)}
              />
              <input
                type="number"
                min={1}
                max={5}
                placeholder="Min rating"
                value={reviewsMinRating}
                onChange={(event) => setReviewsMinRating(event.target.value)}
              />
              <button onClick={() => { setReviewsPage(1); loadReviews(); }}>Apply</button>
            </div>
            {isBusinessSelectionLocked && (
              <p className="google-reviews-ops-muted google-reviews-ops-filter-lock-hint">
                Business and dataset filters are locked to the selected business card. Clear selection to edit manually.
              </p>
            )}

            {loadingReviews ? (
              <p>Loading reviews...</p>
            ) : reviews.length === 0 ? (
              <p className="google-reviews-ops-empty">No reviews match current filters.</p>
            ) : (
              <>
                <div className="google-reviews-ops-table-wrapper">
                  <table className="google-reviews-ops-table">
                    <thead>
                      <tr>
                        <th>Business</th>
                        <th>Author</th>
                        <th>Rating</th>
                        <th>Published</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {reviews.map((review) => (
                        <tr key={review.id}>
                          <td>{review.businessName}</td>
                          <td>{review.author}</td>
                          <td>{review.rating}</td>
                          <td>{review.publishedAt ? new Date(review.publishedAt).toLocaleDateString() : '-'}</td>
                          <td>
                            <button onClick={() => setSelectedReview(review)}>View</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="google-reviews-ops-pagination">
                  <button disabled={reviewsPage <= 1} onClick={() => setReviewsPage((value) => value - 1)}>Previous</button>
                  <span>Page {reviewsPage} of {reviewsTotalPages}</span>
                  <button disabled={reviewsPage >= reviewsTotalPages} onClick={() => setReviewsPage((value) => value + 1)}>Next</button>
                </div>
              </>
            )}
          </section>
        </div>
      </div>

      {selectedReview && (
        <div className="google-reviews-ops-modal-backdrop" onClick={() => setSelectedReview(null)}>
          <div className="google-reviews-ops-modal" onClick={(event) => event.stopPropagation()}>
            <div className="google-reviews-ops-modal-header">
              <h3>{selectedReview.author}</h3>
              <button onClick={() => setSelectedReview(null)}>x</button>
            </div>
            <p><strong>Business:</strong> {selectedReview.businessName}</p>
            <p><strong>Rating:</strong> {selectedReview.rating}</p>
            <p><strong>Published:</strong> {selectedReview.publishedAt ? new Date(selectedReview.publishedAt).toLocaleString() : '-'}</p>
            <p><strong>Lumina score:</strong> {selectedReview.luminaScore ?? '-'}</p>
            <p><strong>Sentiment:</strong> {selectedReview.sentimentAnalysis ?? 'Not analyzed'}</p>
            <p className="google-reviews-ops-modal-text">{selectedReview.text}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleReviewsOpsPage;
